import { GridItem, Heading, SimpleGrid } from "@chakra-ui/layout";
import { InputRightElement, useColorModeValue } from "@chakra-ui/react";
import styled from "@emotion/styled";
import React from "react";
import { FixAndFlipCalculation } from "../../lib/models/short-term/short-term-calculation.model";
import {
  blendedLTCCalc,
  cashToClose,
  finalLoanAmount,
  loanCosts,
  ltcCalcOnPurchaseRehab,
  ltvARVCalc,
  payments,
  rateFeesLLPA,
} from "../../lib/models/short-term/short-term-loan-calculations-map";
import FormSectionHeader from "../common-quote/FormSectionHeader";
import TextInputField from "../form-fields/TextInputField";

function LoanCalculationsAndTerms({
  calculation,
}: {
  calculation: FixAndFlipCalculation;
}) {
  const checkTotal = (checkAmount: number) => {
    if (checkAmount === calculation.LTVARVCalc.totalLoanAmt) {
      return {
        color: "var(--chakra-colors-green-500)",
        textDecoration: "underline",
      };
    }
    return {};
  };
  let updatedLoanCosts = {
    ...calculation.LoanCosts,
    exitFee: calculation.exitFee,
  };

  const labelColor = useColorModeValue(
    "var(--chakra-colors-gray-600)",
    "var(--chakra-colors-gray-400)"
  );

  return (
    <LoanCalculationsSection labelColor={labelColor}>
      <FormSectionHeader
        id="loan-calculation-terms"
        label="Loan Calculations and Terms"
      />

      <div>
        <CalculationSection>
          <Heading fontSize="lg" fontWeight="medium" as="h4">
            Blended LTC Calc
          </Heading>
          <dl>
            <SimpleGrid
              marginTop={6}
              columns={{ base: 1, lg: 4 }}
              spacing={{ base: 6, md: 8, lg: 8 }}
            >
              {blendedLTCCalc.map((calc) => (
                <div key={calc.accessor}>
                  <dt>{calc.label}</dt>
                  <dd
                    style={checkTotal(
                      calculation.BlendedLTCCalc[calc.accessor]
                    )}
                  >
                    {calc.transformer
                      ? calc.transformer(
                          calculation.BlendedLTCCalc[calc.accessor]
                        )
                      : calculation.BlendedLTCCalc[calc.accessor]}
                  </dd>
                </div>
              ))}
              <GridItem colSpan={1}>
                <TextInputField
                  name="adjustedBlendedLTC"
                  label="Adjust Blended LTC?"
                  type="number"
                  children={<InputRightElement children="%" />}
                />
              </GridItem>
            </SimpleGrid>
          </dl>
        </CalculationSection>

        <CalculationSection>
          <Heading fontSize="lg" fontWeight="medium" as="h4">
            LTC Calc on Purchase + Rehab
          </Heading>
          <dl>
            <SimpleGrid
              marginTop={6}
              columns={{ base: 1, lg: 4 }}
              spacing={{ base: 6, md: 8, lg: 8 }}
            >
              {ltcCalcOnPurchaseRehab.map((calc) => (
                <div key={calc.accessor}>
                  <dt>{calc.label}</dt>
                  <dd
                    style={checkTotal(
                      calculation.LTCCalcOnPurchaseRehab[calc.accessor]
                    )}
                  >
                    {calc.transformer
                      ? calc.transformer(
                          calculation.LTCCalcOnPurchaseRehab[calc.accessor]
                        )
                      : calculation.LTCCalcOnPurchaseRehab[calc.accessor]}
                  </dd>
                </div>
              ))}
              <GridItem colSpan={1}>
                <TextInputField
                  name="adjustedPurchaseLTC"
                  label="Adjust Purchase LTC?"
                  type="number"
                  children={<InputRightElement children="%" />}
                />
              </GridItem>
            </SimpleGrid>
          </dl>
        </CalculationSection>

        <CalculationSection>
          <Heading fontSize="lg" fontWeight="medium" as="h4">
            LTV/ARV Calc
          </Heading>
          <dl>
            <SimpleGrid
              marginTop={6}
              columns={{ base: 1, lg: 4 }}
              spacing={{ base: 6, md: 8, lg: 8 }}
            >
              {ltvARVCalc.map((calc) => (
                <div key={calc.accessor}>
                  <dt>{calc.label}</dt>
                  <dd style={checkTotal(calculation.LTVARVCalc[calc.accessor])}>
                    {calc.transformer
                      ? calc.transformer(calculation.LTVARVCalc[calc.accessor])
                      : calculation.LTVARVCalc[calc.accessor]}
                  </dd>
                </div>
              ))}
              <GridItem colSpan={1}>
                <TextInputField
                  name="adjustedARV"
                  label="Adjust ARV?"
                  type="number"
                  children={<InputRightElement children="%" />}
                />
              </GridItem>
            </SimpleGrid>
          </dl>
        </CalculationSection>

        <CalculationSection>
          <Heading fontSize="lg" fontWeight="medium" as="h4">
            Final Loan Amount
          </Heading>
          <dl>
            <SimpleGrid
              marginTop={6}
              columns={{ base: 1, lg: 3 }}
              spacing={{ base: 6, md: 8, lg: 8 }}
            >
              {finalLoanAmount.map((calc) => (
                <div key={calc.accessor}>
                  <dt>{calc.label}</dt>
                  <dd
                    style={
                      calc.accessor === "loanAmt"
                        ? {
                            color: "var(--chakra-colors-green-500)",
                            textDecoration: "underline",
                          }
                        : {}
                    }
                  >
                    {calc.transformer
                      ? calc.transformer(
                          calculation.LTCCalcOnPurchaseRehab[calc.accessor]
                        )
                      : calculation.LTCCalcOnPurchaseRehab[calc.accessor]}
                  </dd>
                </div>
              ))}
            </SimpleGrid>
          </dl>
        </CalculationSection>

        <CalculationSection>
          <Heading fontSize="lg" fontWeight="medium" as="h4">
            Loan Costs
          </Heading>
          <dl>
            <SimpleGrid
              marginTop={6}
              columns={{ base: 1, lg: 3 }}
              spacing={{ base: 6, md: 8, lg: 8 }}
            >
              {loanCosts.map((calc) => (
                <div key={calc.accessor}>
                  <dt>{calc.label}</dt>
                  <dd>
                    {calc.transformer
                      ? calc.transformer(updatedLoanCosts[calc.accessor] ?? 0)
                      : updatedLoanCosts[calc.accessor]}
                  </dd>
                </div>
              ))}
            </SimpleGrid>
          </dl>
        </CalculationSection>

        <CalculationSection>
          <Heading fontSize="lg" fontWeight="medium" as="h4">
            Cash to Close
          </Heading>
          <dl>
            <SimpleGrid
              marginTop={6}
              columns={{ base: 1, lg: 3 }}
              spacing={{ base: 6, md: 8, lg: 8 }}
            >
              {cashToClose.map((calc) => (
                <div key={calc.accessor}>
                  <dt>
                    {calc.showLabelAltOnCondition
                      ? calc.showLabelAltOnCondition(
                          calculation.CashToClose[calc.accessor]
                        )
                        ? calc.labelAlt
                        : calc.label
                      : calc.label}
                  </dt>
                  <dd>
                    {calc.transformer
                      ? calc.transformer(calculation.CashToClose[calc.accessor])
                      : calculation.CashToClose[calc.accessor]}
                  </dd>
                </div>
              ))}
            </SimpleGrid>
          </dl>
        </CalculationSection>

        <CalculationSection>
          <Heading fontSize="lg" fontWeight="medium" as="h4">
            Rate & Fees LLPA's
          </Heading>
          <dl>
            <SimpleGrid
              marginTop={6}
              columns={{ base: 1, lg: 3 }}
              spacing={{ base: 6, md: 8, lg: 8 }}
            >
              {rateFeesLLPA.map((calc) => (
                <div key={calc.accessor}>
                  <dt>{calc.label}</dt>
                  <dd>
                    {calc.transformer
                      ? calc.transformer(calculation.RateFeeLLPA[calc.accessor])
                      : calculation.RateFeeLLPA[calc.accessor]}
                  </dd>
                </div>
              ))}
            </SimpleGrid>
          </dl>
        </CalculationSection>

        <CalculationSection>
          <Heading fontSize="lg" fontWeight="medium" as="h4">
            Payments
          </Heading>
          <dl>
            <SimpleGrid
              marginTop={6}
              columns={{ base: 1, lg: 3 }}
              spacing={{ base: 6, md: 8, lg: 8 }}
            >
              {payments.map((calc) => (
                <div key={calc.accessor}>
                  <dt>{calc.label}</dt>
                  <dd>
                    {calc.transformer
                      ? calc.transformer(calculation.Payments[calc.accessor])
                      : calculation.Payments[calc.accessor]}
                  </dd>
                </div>
              ))}
            </SimpleGrid>
          </dl>
        </CalculationSection>
      </div>
    </LoanCalculationsSection>
  );
}

const LoanCalculationsSection = styled.section<{ labelColor: string }>`
  dl {
    dt {
      letter-spacing: 0.25px;
      color: ${(props) => props.labelColor};
      padding-bottom: var(--chakra-space-2);
      font-size: var(--chakra-fontSizes-sm);
    }
  }
`;

const CalculationSection = styled.section`
  margin-top: var(--chakra-space-10);
`;

export default LoanCalculationsAndTerms;
